import type {
  ProjectAssignmentItem,
  RoutineAssignmentItem,
} from '../types/assignmentPointsNote';
import type {
  GroupMemberPoints,
  MemberCoinRecord,
  MemberItemInstance,
  PointItemNoteDoc,
} from '../types/pointItemNote';
import { emptyGroupMemberPoints } from '../types/pointItemNote';
import {
  buildProjectMemberReachRows,
  buildRoutineMemberReachRows,
} from './assignmentPointsNoteProgress';
import { displayClassMemberName } from './classMemberNameDisplay';

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function newCoinId(): string {
  return `coin-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/** 과제 보상 아이템 인스턴스 안정 ID (지급/잠금 매칭용) */
export function assignmentRewardItemInstanceId(
  assignmentItemId: string,
  memberId: string,
  itemCardId: string
): string {
  return `asgnrw-${assignmentItemId}-${memberId}-${itemCardId}`;
}

function isAssignmentRewardInstance(
  it: MemberItemInstance,
  assignmentItemId: string,
  memberId: string
): boolean {
  if (it.memberId !== memberId || it.category !== 'assignment') return false;
  return it.id.startsWith(`asgnrw-${assignmentItemId}-${memberId}-`);
}

type MemberRef = {
  id: string;
  name: string;
  orderIndex?: number;
  groupId: string;
  nameChoseongPositions?: number[];
};

export type AssignmentRewardMemberLabel = {
  memberId: string;
  label: string;
};

export type AssignmentRewardProcessResult = {
  success: boolean;
  grantedCount: number;
  revokedCount: number;
  message: string;
  /** 이번 처리에서 새로 지급된 멤버 */
  newlyGrantedMembers: AssignmentRewardMemberLabel[];
  /** 이번 처리에서 회수된 멤버 */
  revokedMembers: AssignmentRewardMemberLabel[];
  /** 현재 보상 유지(지급 완료) 멤버 */
  holderMembers: AssignmentRewardMemberLabel[];
  nextGrantedStudentIds: string[];
  nextPointDoc: PointItemNoteDoc;
};

function memberLabel(m: MemberRef): string {
  const name = displayClassMemberName(m).trim() || '(이름 없음)';
  return m.orderIndex != null && m.orderIndex > 0 ? `${m.orderIndex}. ${name}` : name;
}

function emptyFailResult(
  message: string,
  nextGrantedStudentIds: string[],
  nextPointDoc: PointItemNoteDoc
): AssignmentRewardProcessResult {
  return {
    success: false,
    grantedCount: 0,
    revokedCount: 0,
    message,
    newlyGrantedMembers: [],
    revokedMembers: [],
    holderMembers: [],
    nextGrantedStudentIds,
    nextPointDoc,
  };
}

function ensureGroup(
  byGroup: Record<string, GroupMemberPoints>,
  groupId: string
): GroupMemberPoints {
  return byGroup[groupId] ?? emptyGroupMemberPoints();
}

function assignmentRewardCoinMemos(
  title: string,
  kindLabel: '프로젝트' | '루틴'
): { grantMemo: string; revokeMemo: string } {
  return {
    grantMemo: `${title} ${kindLabel} 완료 보상`,
    revokeMemo: `${title} ${kindLabel} 보상 회수`,
  };
}

function netAssignmentRewardCoins(
  group: GroupMemberPoints,
  memberId: string,
  title: string,
  kindLabel: '프로젝트' | '루틴'
): number {
  const { grantMemo, revokeMemo } = assignmentRewardCoinMemos(title, kindLabel);
  let net = 0;
  for (const r of group.coinRecords ?? []) {
    if (r.memberId !== memberId || r.coinType !== 'Assignment') continue;
    if (r.memo === grantMemo || r.memo === revokeMemo) {
      net += r.coinValue;
    }
  }
  return net;
}

function grantOrRevokeMemberReward(
  group: GroupMemberPoints,
  memberId: string,
  opts: {
    mode: 'grant' | 'revoke' | 'unlock';
    assignmentItemId: string;
    title: string;
    kindLabel: '프로젝트' | '루틴';
    rewardCoins: number;
    rewardItemIds: string[];
    recordedDate: string;
  }
): GroupMemberPoints {
  const now = new Date().toISOString();
  let coinRecords = [...(group.coinRecords ?? [])];
  let coinTotals = { ...(group.coinTotals ?? {}) };
  let itemInstances = [...(group.itemInstances ?? [])];
  const { grantMemo, revokeMemo } = assignmentRewardCoinMemos(
    opts.title,
    opts.kindLabel
  );

  const addCoin = (value: number, memo: string) => {
    if (!value) return;
    const rec: MemberCoinRecord = {
      id: newCoinId(),
      memberId,
      coinValue: value,
      coinType: 'Assignment',
      memo,
      recordedDate: opts.recordedDate,
      createdAt: now,
    };
    coinRecords = [rec, ...coinRecords];
    coinTotals[memberId] = (coinTotals[memberId] ?? 0) + value;
  };

  const unlockOrCreateItems = (createMissing: boolean) => {
    opts.rewardItemIds.forEach((itemCardId) => {
      const id = assignmentRewardItemInstanceId(
        opts.assignmentItemId,
        memberId,
        itemCardId
      );
      const existing = itemInstances.find((it) => it.id === id);
      if (existing) {
        itemInstances = itemInstances.map((it) =>
          it.id === id ? { ...it, isLocked: false } : it
        );
        return;
      }
      if (!createMissing) return;
      const inst: MemberItemInstance = {
        id,
        memberId,
        itemCardId,
        category: 'assignment',
        isLocked: false,
        acquiredAt: now,
      };
      itemInstances = [inst, ...itemInstances];
    });
  };

  if (opts.mode === 'grant') {
    if (opts.rewardCoins > 0) {
      // 회수 후 재지급: 부족한 만큼만 보강 (이미 보유 중이면 코인 중복 지급 방지)
      const net = netAssignmentRewardCoins(
        { ...group, coinRecords, coinTotals },
        memberId,
        opts.title,
        opts.kindLabel
      );
      const topUp = opts.rewardCoins - net;
      if (topUp > 0) {
        addCoin(topUp, grantMemo);
      }
    }
    unlockOrCreateItems(true);
  } else if (opts.mode === 'unlock') {
    unlockOrCreateItems(false);
  } else {
    if (opts.rewardCoins > 0) {
      const current = coinTotals[memberId] ?? 0;
      const reclaim = Math.min(current, opts.rewardCoins);
      if (reclaim > 0) {
        addCoin(-reclaim, revokeMemo);
      }
    }
    itemInstances = itemInstances.map((it) => {
      if (isAssignmentRewardInstance(it, opts.assignmentItemId, memberId)) {
        return { ...it, isLocked: true };
      }
      return it;
    });
  }

  return {
    ...group,
    coinRecords,
    coinTotals,
    itemInstances,
  };
}

function applyMemberRewardToPointDoc(
  pointDoc: PointItemNoteDoc,
  member: MemberRef,
  opts: {
    mode: 'grant' | 'revoke' | 'unlock';
    assignmentItemId: string;
    title: string;
    kindLabel: '프로젝트' | '루틴';
    rewardCoins: number;
    rewardItemIds: string[];
  }
): PointItemNoteDoc {
  const groupId = member.groupId;
  if (!groupId) return pointDoc;
  const byGroup = { ...pointDoc.memberPointsByGroup };
  const group = ensureGroup(byGroup, groupId);
  byGroup[groupId] = grantOrRevokeMemberReward(group, member.id, {
    ...opts,
    recordedDate: todayIso(),
  });
  return { ...pointDoc, memberPointsByGroup: byGroup };
}

/** 회수 후 잠긴 과제 보상 아이템이 있으면 true */
function hasLockedAssignmentRewardItems(
  pointDoc: PointItemNoteDoc,
  member: MemberRef,
  assignmentItemId: string
): boolean {
  if (!member.groupId) return false;
  const group = pointDoc.memberPointsByGroup[member.groupId];
  if (!group) return false;
  return (group.itemInstances ?? []).some(
    (it) =>
      isAssignmentRewardInstance(it, assignmentItemId, member.id) && it.isLocked === true
  );
}

/**
 * 기지급으로 표시돼 있어도 실질 보상이 회수된 상태면 재지급 필요.
 * - 잠긴 과제 아이템이 있거나
 * - 해당 과제 Assignment 코인 순합이 보상액 미만(회수 후 잔액 부족)
 */
function memberNeedsAssignmentRegrant(
  pointDoc: PointItemNoteDoc,
  member: MemberRef,
  opts: {
    assignmentItemId: string;
    title: string;
    kindLabel: '프로젝트' | '루틴';
    rewardCoins: number;
  }
): boolean {
  if (hasLockedAssignmentRewardItems(pointDoc, member, opts.assignmentItemId)) {
    return true;
  }
  if (opts.rewardCoins <= 0) return false;
  if (!member.groupId) return false;
  const group = pointDoc.memberPointsByGroup[member.groupId];
  if (!group) return true;
  return (
    netAssignmentRewardCoins(group, member.id, opts.title, opts.kindLabel) <
    opts.rewardCoins
  );
}

/** 미리보기(빈 문서)에서도 기지급+자격 유지 멤버가 있으면 실데이터 조회가 필요 */
export function assignmentRewardNeedsPointDocCheck(
  item: ProjectAssignmentItem | RoutineAssignmentItem,
  members: MemberRef[]
): boolean {
  const currentGranted = new Set(item.rewardGrantedStudentIds ?? []);
  if (currentGranted.size === 0) return false;
  const memberById = new Map(members.map((m) => [m.id, m]));
  const threshold =
    item.kind === 'routine' ? (item.rewardCompletionRate ?? 100) : 100;
  const rates =
    item.kind === 'project'
      ? buildProjectMemberReachRows(item, members)
      : buildRoutineMemberReachRows(item, members);
  const rateById = new Map(rates.map((r) => [r.memberId, r.completionRate]));

  for (const memberId of item.assignedMemberIds) {
    if (!currentGranted.has(memberId)) continue;
    if (!memberById.get(memberId)?.groupId) continue;
    if ((rateById.get(memberId) ?? 0) >= threshold) return true;
  }
  return false;
}

/**
 * 과제 구조 변경 후 기지급 완료 보상만 재계산.
 * 자격 미달·배정 해제 멤버는 회수하고, 새로 자격을 얻은 멤버는 지급하지 않는다.
 */
export function reconcileGrantedAssignmentRewardsInMemory(
  item: ProjectAssignmentItem | RoutineAssignmentItem,
  members: MemberRef[],
  pointDoc: PointItemNoteDoc
): AssignmentRewardProcessResult {
  const currentGranted = item.rewardGrantedStudentIds ?? [];
  if (currentGranted.length === 0) {
    return {
      success: true,
      grantedCount: 0,
      revokedCount: 0,
      message: '',
      newlyGrantedMembers: [],
      revokedMembers: [],
      holderMembers: [],
      nextGrantedStudentIds: [],
      nextPointDoc: pointDoc,
    };
  }

  const kindLabel: '프로젝트' | '루틴' = item.kind === 'project' ? '프로젝트' : '루틴';
  const title = item.title || (item.kind === 'project' ? '프로젝트' : '루틴');
  const assigned = new Set(item.assignedMemberIds);
  const memberById = new Map(members.map((m) => [m.id, m]));
  const rewardCoins = item.rewardCoins ?? 0;
  const rewardItemIds = item.rewardItemIds ?? [];
  const threshold =
    item.kind === 'routine' ? (item.rewardCompletionRate ?? 100) : 100;
  const rateById = new Map<string, number>();
  if (item.rewardEnabled) {
    const rates =
      item.kind === 'project'
        ? buildProjectMemberReachRows(item, members)
        : buildRoutineMemberReachRows(item, members);
    rates.forEach((r) => rateById.set(r.memberId, r.completionRate));
  }

  const nextGranted: string[] = [];
  const revokedMembers: AssignmentRewardMemberLabel[] = [];
  let revokedCount = 0;
  let nextPointDoc = pointDoc;

  for (const memberId of currentGranted) {
    const stillAssigned = assigned.has(memberId);
    const eligible = item.rewardEnabled
      ? (rateById.get(memberId) ?? 0) >= threshold
      : true;
    if (stillAssigned && eligible) {
      nextGranted.push(memberId);
      continue;
    }
    const member = memberById.get(memberId);
    if (member?.groupId) {
      nextPointDoc = applyMemberRewardToPointDoc(nextPointDoc, member, {
        mode: 'revoke',
        assignmentItemId: item.id,
        title,
        kindLabel,
        rewardCoins,
        rewardItemIds,
      });
      revokedMembers.push({ memberId, label: memberLabel(member) });
      revokedCount += 1;
    }
  }

  const holderMembers = nextGranted
    .map((id) => {
      const m = memberById.get(id);
      return m ? { memberId: id, label: memberLabel(m) } : null;
    })
    .filter((x): x is AssignmentRewardMemberLabel => x != null);

  return {
    success: true,
    grantedCount: 0,
    revokedCount,
    message: revokedCount > 0 ? `회수 ${revokedCount}명` : '',
    newlyGrantedMembers: [],
    revokedMembers,
    holderMembers,
    nextGrantedStudentIds: nextGranted,
    nextPointDoc,
  };
}

/** 프로젝트: 하위과제 전부 완료(도달률 100%)인 멤버에게 지급, 미달·기지급은 회수 */
export function processProjectAssignmentRewardsInMemory(
  item: ProjectAssignmentItem,
  members: MemberRef[],
  pointDoc: PointItemNoteDoc
): AssignmentRewardProcessResult {
  if (!item.rewardEnabled) {
    return emptyFailResult(
      '프로젝트 보상이 비활성화되어 있습니다.',
      item.rewardGrantedStudentIds ?? [],
      pointDoc
    );
  }
  const rewardCoins = item.rewardCoins ?? 0;
  const rewardItemIds = item.rewardItemIds ?? [];
  if (rewardCoins <= 0 && rewardItemIds.length === 0) {
    return emptyFailResult(
      '설정된 보상이 없습니다.',
      item.rewardGrantedStudentIds ?? [],
      pointDoc
    );
  }

  const rates = buildProjectMemberReachRows(item, members);
  const rateById = new Map(rates.map((r) => [r.memberId, r.completionRate]));
  const memberById = new Map(members.map((m) => [m.id, m]));
  const currentGranted = new Set(item.rewardGrantedStudentIds ?? []);
  const nextGranted: string[] = [];
  const newlyGrantedMembers: AssignmentRewardMemberLabel[] = [];
  const revokedMembers: AssignmentRewardMemberLabel[] = [];
  let grantedCount = 0;
  let revokedCount = 0;
  let nextPointDoc = pointDoc;

  for (const memberId of item.assignedMemberIds) {
    const member = memberById.get(memberId);
    if (!member?.groupId) continue;
    const eligible = (rateById.get(memberId) ?? 0) >= 100;
    const already = currentGranted.has(memberId);
    const label = { memberId, label: memberLabel(member) };

    if (eligible && !already) {
      nextPointDoc = applyMemberRewardToPointDoc(nextPointDoc, member, {
        mode: 'grant',
        assignmentItemId: item.id,
        title: item.title || '프로젝트',
        kindLabel: '프로젝트',
        rewardCoins,
        rewardItemIds,
      });
      nextGranted.push(memberId);
      newlyGrantedMembers.push(label);
      grantedCount += 1;
    } else if (!eligible && already) {
      nextPointDoc = applyMemberRewardToPointDoc(nextPointDoc, member, {
        mode: 'revoke',
        assignmentItemId: item.id,
        title: item.title || '프로젝트',
        kindLabel: '프로젝트',
        rewardCoins,
        rewardItemIds,
      });
      revokedMembers.push(label);
      revokedCount += 1;
    } else if (eligible && already) {
      const title = item.title || '프로젝트';
      const needsRegrant = memberNeedsAssignmentRegrant(nextPointDoc, member, {
        assignmentItemId: item.id,
        title,
        kindLabel: '프로젝트',
        rewardCoins,
      });
      if (needsRegrant || rewardItemIds.length > 0) {
        nextPointDoc = applyMemberRewardToPointDoc(nextPointDoc, member, {
          mode: needsRegrant ? 'grant' : 'unlock',
          assignmentItemId: item.id,
          title,
          kindLabel: '프로젝트',
          rewardCoins,
          rewardItemIds,
        });
      }
      if (needsRegrant) {
        newlyGrantedMembers.push(label);
        grantedCount += 1;
      }
      nextGranted.push(memberId);
    }
  }

  const holderMembers = nextGranted
    .map((id) => {
      const m = memberById.get(id);
      return m ? { memberId: id, label: memberLabel(m) } : null;
    })
    .filter((x): x is AssignmentRewardMemberLabel => x != null);

  let message = `보상지급 ${grantedCount}명`;
  if (revokedCount > 0) message += `, 회수 ${revokedCount}명`;
  message += `\n총 보상지급자 수: ${nextGranted.length}명`;

  return {
    success: true,
    grantedCount,
    revokedCount,
    message,
    newlyGrantedMembers,
    revokedMembers,
    holderMembers,
    nextGrantedStudentIds: nextGranted,
    nextPointDoc,
  };
}

/** 루틴: 도달률 ≥ rewardCompletionRate 멤버에게 지급, 미달·기지급은 회수 */
export function processRoutineAssignmentRewardsInMemory(
  item: RoutineAssignmentItem,
  members: MemberRef[],
  pointDoc: PointItemNoteDoc
): AssignmentRewardProcessResult {
  if (!item.rewardEnabled) {
    return emptyFailResult(
      '루틴 과제 보상이 비활성화되어 있습니다.',
      item.rewardGrantedStudentIds ?? [],
      pointDoc
    );
  }
  const rewardCoins = item.rewardCoins ?? 0;
  const rewardItemIds = item.rewardItemIds ?? [];
  if (rewardCoins <= 0 && rewardItemIds.length === 0) {
    return emptyFailResult(
      '설정된 보상이 없습니다.',
      item.rewardGrantedStudentIds ?? [],
      pointDoc
    );
  }

  const threshold = item.rewardCompletionRate ?? 100;
  const rates = buildRoutineMemberReachRows(item, members);
  const rateById = new Map(rates.map((r) => [r.memberId, r.completionRate]));
  const memberById = new Map(members.map((m) => [m.id, m]));
  const currentGranted = new Set(item.rewardGrantedStudentIds ?? []);
  const nextGranted: string[] = [];
  const newlyGrantedMembers: AssignmentRewardMemberLabel[] = [];
  const revokedMembers: AssignmentRewardMemberLabel[] = [];
  let grantedCount = 0;
  let revokedCount = 0;
  let nextPointDoc = pointDoc;

  for (const memberId of item.assignedMemberIds) {
    const member = memberById.get(memberId);
    if (!member?.groupId) continue;
    const eligible = (rateById.get(memberId) ?? 0) >= threshold;
    const already = currentGranted.has(memberId);
    const label = { memberId, label: memberLabel(member) };

    if (eligible && !already) {
      nextPointDoc = applyMemberRewardToPointDoc(nextPointDoc, member, {
        mode: 'grant',
        assignmentItemId: item.id,
        title: item.title || '루틴',
        kindLabel: '루틴',
        rewardCoins,
        rewardItemIds,
      });
      nextGranted.push(memberId);
      newlyGrantedMembers.push(label);
      grantedCount += 1;
    } else if (!eligible && already) {
      nextPointDoc = applyMemberRewardToPointDoc(nextPointDoc, member, {
        mode: 'revoke',
        assignmentItemId: item.id,
        title: item.title || '루틴',
        kindLabel: '루틴',
        rewardCoins,
        rewardItemIds,
      });
      revokedMembers.push(label);
      revokedCount += 1;
    } else if (eligible && already) {
      const title = item.title || '루틴';
      const needsRegrant = memberNeedsAssignmentRegrant(nextPointDoc, member, {
        assignmentItemId: item.id,
        title,
        kindLabel: '루틴',
        rewardCoins,
      });
      if (needsRegrant || rewardItemIds.length > 0) {
        nextPointDoc = applyMemberRewardToPointDoc(nextPointDoc, member, {
          mode: needsRegrant ? 'grant' : 'unlock',
          assignmentItemId: item.id,
          title,
          kindLabel: '루틴',
          rewardCoins,
          rewardItemIds,
        });
      }
      if (needsRegrant) {
        newlyGrantedMembers.push(label);
        grantedCount += 1;
      }
      nextGranted.push(memberId);
    }
  }

  const holderMembers = nextGranted
    .map((id) => {
      const m = memberById.get(id);
      return m ? { memberId: id, label: memberLabel(m) } : null;
    })
    .filter((x): x is AssignmentRewardMemberLabel => x != null);

  let message = `보상지급 ${grantedCount}명`;
  if (revokedCount > 0) message += `, 회수 ${revokedCount}명`;
  message += `\n총 보상지급자 수: ${nextGranted.length}명`;

  return {
    success: true,
    grantedCount,
    revokedCount,
    message,
    newlyGrantedMembers,
    revokedMembers,
    holderMembers,
    nextGrantedStudentIds: nextGranted,
    nextPointDoc,
  };
}
